import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTable, useSortBy } from 'react-table';

export default function AlertDetail({ alert, onEdit, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (alert && alert.id) {
      fetchAlertHistory(alert.id);
    }
  }, [alert]);

  const fetchAlertHistory = async (alertId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/alerts/${alertId}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching alert history:', error);
      toast.error('Failed to load alert execution history');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAlert = async () => {
    try {
      setLoading(true);
      await axios.post(`/api/alerts/${alert.id}/run`);
      toast.success('Alert execution triggered successfully!');
      // Refresh history after a short delay to allow execution to complete
      setTimeout(() => fetchAlertHistory(alert.id), 1000);
    } catch (error) {
      console.error('Error running alert:', error);
      const errorMessage = error.response?.data?.detail || 'An error occurred while running the alert';
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'Run Start',
        accessor: 'run_start_time',
        Cell: ({ value }) => new Date(value).toLocaleString()
      },
      {
        Header: 'Run End',
        accessor: 'run_end_time',
        Cell: ({ value }) => new Date(value).toLocaleString()
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded text-xs font-medium ${value === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value.toUpperCase()}
          </span>
        )
      },
      {
        Header: 'Affected Nodes',
        accessor: 'affected_nodes_count',
        Cell: ({ value }) => <span className="font-mono">{value}</span>
      },
      {
        Header: 'Execution Time (s)',
        accessor: 'execution_time_seconds',
        Cell: ({ value }) => <span className="font-mono">{value}</span>
      },
      {
        Header: 'Error Message',
        accessor: 'error_message',
        Cell: ({ value }) => (
          value ? (
            <div className="max-w-xs truncate" title={value}>
              <span className="text-red-600">{value}</span>
            </div>
          ) : (
            <span className="text-gray-400">None</span>
          )
        )
      }
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data: history,
      initialState: {
        sortBy: [{ id: 'run_start_time', desc: true }]
      }
    },
    useSortBy
  );

  if (!alert) return null;

  return (
    <div className="bg-white shadow rounded p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Alert Details: {alert.name}
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={handleRunAlert}
            disabled={loading}
            className="bg-purple-l mr-2 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {loading ? 'Running...' : 'Run Now'}
          </button>
          <button
            onClick={() => onEdit(alert)}
            className="bg-purple-l mr-2 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Edit
          </button>
          <button
            onClick={onClose}
            className="bg-purple-l hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            Close
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">Alert Configuration</h3>
          
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-500">Description</p>
            <p className="mt-1">{alert.description || 'No description provided'}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Node Type</p>
              <p className="mt-1 font-medium">{alert.node_type}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Query Type</p>
              <p className="mt-1 font-medium">{alert.query_type.toUpperCase()}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Schedule</p>
              <p className="mt-1 font-medium">{alert.schedule_type.charAt(0).toUpperCase() + alert.schedule_type.slice(1)}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1">
                <span className={`px-2 py-1 rounded text-sm font-medium ${alert.is_active ? 'bg-purple-l text-white' : 'bg-gray-700 text-white'}`}>
                  {alert.is_active ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Created</p>
              <p className="mt-1">{new Date(alert.created_at).toLocaleString()}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Last Updated</p>
              <p className="mt-1">{new Date(alert.updated_at).toLocaleString()}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Last Run</p>
              <p className="mt-1">{alert.last_run_date ? new Date(alert.last_run_date).toLocaleString() : 'Never'}</p>
            </div>

          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">Query</h3>
          <pre className="bg-gray-100 p-4 rounded font-mono text-sm overflow-auto max-h-64">
            {alert.query}
          </pre>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">Execution History</h3>
        
        {loading ? (
          <div className="text-center py-4">Loading history...</div>
        ) : (
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <div className="flex items-center">
                          {column.render('Header')}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? ' 🔽'
                                : ' 🔼'
                              : ''}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                {rows.length > 0 ? (
                  rows.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50">
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            className="px-6 py-4 whitespace-nowrap text-sm"
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-4 text-center text-sm text-gray-500">
                      No execution history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
} 